import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, AlertTriangle, CheckCircle2, FileSpreadsheet, Search } from "lucide-react";
import { toast } from "sonner";
import { authClient } from "../../lib/AuthContext";
import { money } from "../../lib/jobCalc";

export const AdminInvoiceAudit = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      try { setData((await authClient.get("/admin/invoice-audit")).data); }
      catch { toast.error("Could not load audit"); }
      finally { setLoading(false); }
    })();
  }, []);

  const rows = (data?.rows || []).filter((r) =>
    !filter.trim() || [r.tax_invoice_number, r.client_name, r.customer_code, String(r.job_number)]
      .some((v) => (v || "").toString().toLowerCase().includes(filter.trim().toLowerCase()))
  );

  const csvUrl = () => {
    const header = ["INV", "Job#", "Date", "Payer", "Customer", "Client", "Amount", "Status", "Voided"];
    const body = (data?.rows || []).map((r) => [
      r.tax_invoice_number, r.job_number, (r.issued_at || "").slice(0, 19), r.payer_type,
      r.customer_code || "", (r.client_name || "").replace(/,/g, " "), r.amount, r.invoice_status || "", r.voided ? "yes" : "no",
    ].join(","));
    const csv = [header.join(","), ...body].join("\n");
    return `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  };

  if (loading) return <div className="p-10 text-center text-[#3f4b52]"><Loader2 className="animate-spin inline-block mr-2" size={16} /> Loading…</div>;

  const s = data.summary;
  const healthy = s.gaps.length === 0 && s.duplicates.length === 0;

  return (
    <div data-testid="admin-invoice-audit-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Invoice number audit</h1>
          <p className="text-[#3f4b52] mt-1">SARS-friendly gap detection for the sequential Tax Invoice series.</p>
        </div>
        <a href={csvUrl()} download="invoice-audit.csv" data-testid="audit-csv"
          className="bg-[#10b981] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2">
          <FileSpreadsheet size={14} /> Export CSV
        </a>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card label="First INV" value={s.first || "—"} />
        <Card label="Last INV" value={s.last || "—"} />
        <Card label="Total issued" value={s.count} />
        <Card label="Voided" value={s.voided} color={s.voided > 0 ? "#dc2626" : "#111619"} />
        <Card label="Next expected" value={s.next_expected} color="#f28a16" />
      </div>

      {healthy ? (
        <div className="mt-4 bg-[#ecfdf5] border border-[#10b981] rounded-md p-4 inline-flex items-center gap-2 text-sm text-[#065f46]" data-testid="audit-healthy">
          <CheckCircle2 size={16} /> No gaps or duplicates — sequence is intact.
        </div>
      ) : (
        <div className="mt-4 bg-[#fef2f2] border border-[#dc2626] rounded-md p-4 text-sm text-[#7f1d1d]" data-testid="audit-issues">
          <div className="inline-flex items-center gap-2 font-bold"><AlertTriangle size={16} /> Sequence exceptions</div>
          {s.gaps.length > 0 && <div className="mt-1">Missing: <b className="font-mono">{s.gaps.join(", ")}</b></div>}
          {s.duplicates.length > 0 && <div className="mt-1">Duplicates: <b className="font-mono">{s.duplicates.join(", ")}</b></div>}
        </div>
      )}

      <div className="mt-4 relative max-w-md">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa6ad]" />
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by INV, job#, customer…" className="w-full pl-9 h-10 bg-white border border-[#cfd7db] rounded-md text-sm" />
      </div>

      <div className="mt-4 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#edf2f4] text-[#3f4b52] uppercase tracking-widest text-xs">
              <tr><th className="text-left px-4 py-2">INV</th><th className="text-left px-4 py-2">Job</th><th className="text-left px-4 py-2">Issued</th><th className="text-left px-4 py-2">Payer</th><th className="text-left px-4 py-2">Customer</th><th className="text-right px-4 py-2">Amount</th><th className="text-left px-4 py-2">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-[#eef2f4]">
              {rows.map((r) => (
                <tr key={r.tax_invoice_number} className={r.voided ? "bg-[#fef2f2]" : r.duplicate ? "bg-[#fff7ed]" : ""} data-testid={`audit-row-${r.tax_invoice_number}`}>
                  <td className="px-4 py-2 font-mono font-bold text-[#f28a16]">{r.tax_invoice_number}</td>
                  <td className="px-4 py-2"><Link to={`/admin/jobs/${r.job_id}`} className="text-[#3f4b52] hover:text-[#f28a16]">#{r.job_number}</Link></td>
                  <td className="px-4 py-2 text-xs text-[#3f4b52]">{(r.issued_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-2 text-xs uppercase">{r.payer_type}</td>
                  <td className="px-4 py-2 text-xs">{r.customer_code || <span className="italic text-[#9aa6ad]">walk-in</span>}</td>
                  <td className="px-4 py-2 text-right font-bold">{money(r.amount)}</td>
                  <td className="px-4 py-2 text-xs uppercase">
                    {r.voided ? <span className="text-[#dc2626] font-bold">VOID</span> : r.invoice_status || "issued"}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-[#3f4b52]">No invoices match.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Card = ({ label, value, color = "#111619" }) => (
  <div className="bg-white border border-[#cfd7db] rounded-md p-4">
    <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest">{label}</div>
    <div className="font-display font-black text-2xl mt-0.5 font-mono" style={{ color }}>{value}</div>
  </div>
);

export default AdminInvoiceAudit;
